import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, Upload, Sparkles, FileText, Trash2, CheckCircle2, AlertCircle,
  Image, FileAudio, File, AlertTriangle, X, DollarSign, FileDown, ClipboardList,
  Shield, FileCode, Users, BarChart3, Award, Calculator, Plus, RefreshCw,
  ChevronRight, ChevronDown, Eye, Edit3, Loader2, History, ListTree,
  ScrollText, MessageSquare, NotebookPen, BadgeCheck, Inbox, GitBranch
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Checkbox } from "../components/ui/checkbox";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Reordered: Scope Closing inserted between Compliance and Cost Codes
const WORKFLOW_STEPS = [
  { id: 0, name: "Setup", icon: FileText },
  { id: 1, name: "Scope", icon: ClipboardList },
  { id: 2, name: "Hidden Work", icon: AlertTriangle },
  { id: 3, name: "Compliance", icon: Shield },
  { id: 4, name: "Scope Closing", icon: BadgeCheck },
  { id: 5, name: "Cost Codes", icon: FileCode },
  { id: 6, name: "Pricing", icon: DollarSign },
  { id: 7, name: "Bidding", icon: Users },
  { id: 8, name: "Leveling", icon: Award },
  { id: 9, name: "Final", icon: Calculator },
];

const CONTRACT_TYPES = [
  { value: "cost_plus", label: "Cost Plus" },
  { value: "lump_sum", label: "Lump Sum" },
  { value: "hybrid", label: "Hybrid" },
  { value: "gmp", label: "GMP" },
];

const TRADES = [
  "demolition", "structural", "electrical", "plumbing", "hvac",
  "waterproofing", "finishes", "flooring", "fixtures", "painting",
  "carpentry", "masonry", "roofing", "sitework", "permits", "general"
];

const EFFORT_TYPES = ["man-hour", "inspection", "contracting", "material", "equipment", "permit"];
const EFFORT_UNITS = ["HR", "EA", "DAY", "LS"];

const fmtCurrency = (v) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", minimumFractionDigits: 0 }).format(v || 0);

function FileIcon({ name }) {
  const ext = (name || "").split(".").pop().toLowerCase();
  if (["jpg", "jpeg", "png", "webp"].includes(ext)) return <Image className="w-4 h-4 text-purple-500" />;
  if (["mp3", "wav", "ogg", "m4a", "opus"].includes(ext)) return <FileAudio className="w-4 h-4 text-blue-500" />;
  if (ext === "pdf") return <FileText className="w-4 h-4 text-red-500" />;
  return <File className="w-4 h-4 text-gray-500" />;
}

function LoadingButton({ loading, children, ...props }) {
  return (
    <Button {...props} disabled={loading || props.disabled}>
      {loading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Processing...</> : children}
    </Button>
  );
}

export default function CaseView() {
  const { caseId } = useParams();
  const navigate = useNavigate();
  const [caseData, setCaseData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [dragActive, setDragActive] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [vendors, setVendors] = useState([]);
  const [wbsTree, setWbsTree] = useState([]);
  const [historicalProjects, setHistoricalProjects] = useState([]);
  const [historyPopup, setHistoryPopup] = useState(null);
  const [changeLogOpen, setChangeLogOpen] = useState(false);
  const [candidatesOpen, setCandidatesOpen] = useState(false);
  const [revisionsOpen, setRevisionsOpen] = useState(false);
  const [applicableRules, setApplicableRules] = useState({ rules: [], merged_actions: {} });

  const fetchCase = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cases/${caseId}`);
      setCaseData(res.data);
      setActiveStep(res.data.current_step || 0);
    } catch (e) { toast.error("Failed to load case"); }
    finally { setLoading(false); }
  }, [caseId]);

  useEffect(() => {
    fetchCase();
    axios.get(`${API}/vendors`).then(r => setVendors(r.data)).catch(() => {});
    axios.get(`${API}/wbs-tree`).then(r => setWbsTree(r.data.nodes)).catch(() => {});
    axios.get(`${API}/historical-projects`).then(r => setHistoricalProjects(r.data)).catch(() => {});
    axios.get(`${API}/cases/${caseId}/applicable-rules`).then(r => setApplicableRules(r.data)).catch(() => {});
  }, [fetchCase]);

  // ============== FILES ==============
  const uploadFiles = async (files) => {
    setUploading(true);
    const fd = new FormData();
    for (let i = 0; i < files.length; i++) fd.append("files", files[i]);
    try {
      await axios.post(`${API}/cases/${caseId}/files`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${files.length} file(s) uploaded`);
      fetchCase();
    } catch { toast.error("Upload failed"); }
    finally { setUploading(false); }
  };
  const deleteFile = async (id) => { try { await axios.delete(`${API}/cases/${caseId}/files/${id}`); fetchCase(); } catch { toast.error("Delete failed"); } };

  // ============== SCOPE ==============
  const analyzeScope = async () => {
    if (!caseData?.files?.length && !caseData?.description) { toast.error("Upload files or add description first"); return; }
    setProcessing(true);
    try { await axios.post(`${API}/cases/${caseId}/analyze-scope`); toast.success("Scope extracted!"); fetchCase(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setProcessing(false); }
  };

  const updateScopeItem = async (itemId, patch) => {
    try { await axios.put(`${API}/cases/${caseId}/scope/${itemId}`, patch); fetchCase(); }
    catch { toast.error("Update failed"); }
  };

  const addScopeItem = async (payload) => {
    try { await axios.post(`${API}/cases/${caseId}/scope-items`, payload); toast.success("Item added"); fetchCase(); }
    catch { toast.error("Add failed"); }
  };
  const deleteScopeItem = async (id) => {
    try { await axios.delete(`${API}/cases/${caseId}/scope-items/${id}`); fetchCase(); }
    catch { toast.error("Delete failed"); }
  };

  const revalidateScope = async () => {
    setProcessing(true);
    try { const r = await axios.post(`${API}/cases/${caseId}/revalidate-scope`); toast.success(`Revalidated. ${r.data.applied} adjusted`); fetchCase(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setProcessing(false); }
  };

  // ============== HIDDEN WORK ==============
  const detectHiddenWork = async () => {
    setProcessing(true);
    try { await axios.post(`${API}/cases/${caseId}/detect-hidden-work`); toast.success("Hidden work detected"); fetchCase(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setProcessing(false); }
  };
  const updateHiddenWork = async (id, patch) => {
    try { await axios.put(`${API}/cases/${caseId}/hidden-work/${id}`, patch); fetchCase(); }
    catch { toast.error("Update failed"); }
  };

  // ============== COMPLIANCE ==============
  const checkCompliance = async () => {
    setProcessing(true);
    try { await axios.post(`${API}/cases/${caseId}/check-compliance`); toast.success("Compliance reviewed"); fetchCase(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setProcessing(false); }
  };
  const updateCompliance = async (id, accepted, notes) => {
    const fd = new FormData(); fd.append("accepted", accepted); fd.append("notes", notes || "");
    try { await axios.put(`${API}/cases/${caseId}/compliance/${id}`, fd); fetchCase(); }
    catch { toast.error("Update failed"); }
  };

  // ============== COST CODES ==============
  const mapCostCodes = async () => {
    setProcessing(true);
    try { await axios.post(`${API}/cases/${caseId}/map-cost-codes`); toast.success("Codes mapped"); fetchCase(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setProcessing(false); }
  };
  const updateItemCodes = async (itemId, costCode, revenueCode) => {
    const fd = new FormData(); fd.append("cost_code", costCode); fd.append("revenue_code", revenueCode || costCode);
    try { await axios.put(`${API}/cases/${caseId}/scope/${itemId}/codes`, fd); fetchCase(); }
    catch { toast.error("Update failed"); }
  };

  // ============== PRICING (macro + override) ==============
  const setMacroPricing = async (list) => {
    try { await axios.put(`${API}/cases/${caseId}/macro-pricing`, list); toast.success("Macro pricing saved"); fetchCase(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const setPricingStrategy = async () => {
    setProcessing(true);
    try { await axios.post(`${API}/cases/${caseId}/set-pricing-strategy`); toast.success("Strategy proposed by AI"); fetchCase(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setProcessing(false); }
  };
  const overrideItemPricing = async (itemId, patch) => {
    const fd = new FormData();
    Object.entries(patch).forEach(([k, v]) => v !== undefined && v !== null && fd.append(k, v));
    try { await axios.put(`${API}/cases/${caseId}/scope/${itemId}/pricing-override`, fd); fetchCase(); }
    catch { toast.error("Update failed"); }
  };

  // ============== BIDDING ==============
  const createBidPackage = async (payload) => {
    try { await axios.post(`${API}/cases/${caseId}/bid-packages`, payload); toast.success("Package created"); fetchCase(); }
    catch { toast.error("Create failed"); }
  };
  const openBidPackage = async (pkgId) => {
    try { await axios.put(`${API}/cases/${caseId}/bid-packages/${pkgId}/open`); toast.success("Package opened"); fetchCase(); }
    catch { toast.error("Open failed"); }
  };

  const equalizeBids = async (pkgId) => {
    setProcessing(true);
    try { await axios.post(`${API}/cases/${caseId}/bid-packages/${pkgId}/equalize`); toast.success("Leveling complete"); fetchCase(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setProcessing(false); }
  };
  const awardBid = async (pkgId, vendorId) => {
    try { await axios.post(`${API}/cases/${caseId}/bid-packages/${pkgId}/award/${vendorId}`); toast.success("Awarded"); fetchCase(); }
    catch { toast.error("Award failed"); }
  };

  // ============== SCENARIOS ==============
  const computeScenarios = async (contingencyPct, overrides, rationale, itemContingency = {}) => {
    setProcessing(true);
    try { await axios.post(`${API}/cases/${caseId}/scenarios`, { contingency_percent: contingencyPct, overrides, item_contingency: itemContingency, rationale }); toast.success("Scenarios computed"); fetchCase(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setProcessing(false); }
  };

  // ============== EXPORT ==============
  const exportProposal = async () => {
    try {
      const res = await axios.get(`${API}/cases/${caseId}/export-proposal`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = `proposal_${caseId}.docx`; a.click();
      toast.success("Proposal downloaded");
    } catch { toast.error("Export failed"); }
  };

  // ============== NOTES ==============
  const updateItemNotes = async (itemId, patch) => {
    try { await axios.put(`${API}/cases/${caseId}/scope/${itemId}/notes`, patch); toast.success("Notes saved"); fetchCase(); }
    catch { toast.error("Save failed"); }
  };

  // ============== MEETING DECISIONS ==============
  const tagFile = async (fileId, fileType) => {
    const fd = new FormData(); fd.append("file_type", fileType);
    try { await axios.put(`${API}/cases/${caseId}/files/${fileId}/tag`, fd); toast.success("Tagged"); fetchCase(); }
    catch { toast.error("Tag failed"); }
  };
  const extractDecisions = async (fileId) => {
    setProcessing(true);
    try { const r = await axios.post(`${API}/cases/${caseId}/files/${fileId}/extract-decisions`); toast.success(`${r.data.candidates.length} decisions extracted`); fetchCase(); }
    catch (e) { toast.error(e.response?.data?.detail || "Extract failed"); }
    finally { setProcessing(false); }
  };
  const decideCandidate = async (candidateId, action) => {
    const fd = new FormData(); fd.append("action", action);
    try { await axios.put(`${API}/cases/${caseId}/decision-candidates/${candidateId}`, fd); fetchCase(); }
    catch { toast.error("Failed"); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  if (!caseData) return <div className="text-center py-8">Case not found</div>;

  return (
    <div className="space-y-6 max-w-7xl mx-auto" data-testid="case-view">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <Button variant="ghost" size="sm" className="mb-2 gap-2" onClick={() => navigate("/")}>
            <ArrowLeft className="w-4 h-4" /> Dashboard
          </Button>
          <h1 className="text-2xl lg:text-3xl font-heading font-bold truncate" data-testid="case-title">{caseData.name}</h1>
          <p className="text-muted-foreground text-sm">{caseData.client_name} · {caseData.address}</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" onClick={() => setRevisionsOpen(true)} className="gap-2" data-testid="open-revisions-btn">
            <GitBranch className="w-4 h-4" /> Revisions
          </Button>
          <Button variant="outline" onClick={() => setCandidatesOpen(true)} className="gap-2" data-testid="open-candidates-btn">
            <Inbox className="w-4 h-4" /> Decisions
            {(caseData.decision_candidates || []).filter(c => c.status === "pending").length > 0 && (
              <Badge className="bg-amber-100 text-amber-700">{(caseData.decision_candidates || []).filter(c => c.status === "pending").length}</Badge>
            )}
          </Button>
          <Button variant="outline" onClick={() => setChangeLogOpen(true)} className="gap-2" data-testid="open-changelog-btn">
            <ScrollText className="w-4 h-4" /> Change Log
          </Button>
          <Button variant="outline" onClick={exportProposal} className="gap-2" data-testid="export-proposal-btn">
            <FileDown className="w-4 h-4" /> Export Word
          </Button>
        </div>
      </div>

      {/* Applicable rules banner */}
      {applicableRules.rules.length > 0 && (
        <div className="swiss-card border-l-4 border-l-primary py-2" data-testid="rules-banner">
          <details>
            <summary className="cursor-pointer flex items-center gap-2 text-sm">
              <Sparkles className="w-4 h-4 text-primary" />
              <span className="font-medium">{applicableRules.rules.length} company rule(s) apply to this project</span>
              <span className="text-xs text-muted-foreground ml-2">
                {applicableRules.merged_actions.fee_percent != null && `Fee ${applicableRules.merged_actions.fee_percent}% · `}
                {applicableRules.merged_actions.margin_percent != null && `Margin ${applicableRules.merged_actions.margin_percent}% · `}
                {applicableRules.merged_actions.contingency_percent != null && `Cont ${applicableRules.merged_actions.contingency_percent}%`}
              </span>
            </summary>
            <ul className="mt-2 ml-6 space-y-1 text-sm">
              {applicableRules.rules.map(r => (
                <li key={r.id} className="flex items-center gap-2" data-testid={`applied-rule-${r.id}`}>
                  <Badge variant="outline" className="font-mono text-xs">P{r.priority}</Badge>
                  <span className="font-medium">{r.name}</span>
                  {r.description && <span className="text-xs text-muted-foreground">— {r.description}</span>}
                </li>
              ))}
            </ul>
          </details>
        </div>
      )}

      {/* Progress Bar */}
      <div className="swiss-card overflow-x-auto">
        <div className="flex items-center min-w-max gap-1">
          {WORKFLOW_STEPS.map((s) => {
            const Icon = s.icon;
            const done = s.id < (caseData.current_step || 0);
            const active = s.id === activeStep;
            return (
              <button
                key={s.id}
                onClick={() => setActiveStep(s.id)}
                className={`flex flex-col items-center gap-1 p-2 rounded transition-colors min-w-[80px]
                  ${active ? "bg-primary text-primary-foreground" : done ? "text-green-600" : "text-muted-foreground hover:bg-muted"}`}
                data-testid={`step-tab-${s.id}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-xs font-medium">{s.id}. {s.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      {activeStep === 0 && <Step0Setup caseData={caseData} uploadFiles={uploadFiles} deleteFile={deleteFile} uploading={uploading} dragActive={dragActive} setDragActive={setDragActive} analyzeScope={analyzeScope} processing={processing} />}
      {activeStep === 1 && <Step1Scope caseData={caseData} updateScopeItem={updateScopeItem} addScopeItem={addScopeItem} deleteScopeItem={deleteScopeItem} revalidateScope={revalidateScope} analyzeScope={analyzeScope} processing={processing} />}
      {activeStep === 2 && <Step2HiddenWork caseData={caseData} detectHiddenWork={detectHiddenWork} updateHiddenWork={updateHiddenWork} processing={processing} />}
      {activeStep === 3 && <Step3Compliance caseData={caseData} checkCompliance={checkCompliance} updateCompliance={updateCompliance} processing={processing} />}
      {activeStep === 4 && <Step4ScopeClosing caseId={caseId} caseData={caseData} fetchCase={fetchCase} />}
      {activeStep === 5 && <Step4CostCodes caseData={caseData} mapCostCodes={mapCostCodes} updateItemCodes={updateItemCodes} wbsTree={wbsTree} processing={processing} />}
      {activeStep === 6 && <Step5Pricing caseData={caseData} setMacroPricing={setMacroPricing} setPricingStrategy={setPricingStrategy} overrideItemPricing={overrideItemPricing} updateItemNotes={updateItemNotes} wbsTree={wbsTree} processing={processing} />}
      {activeStep === 7 && <Step6Bidding caseData={caseData} vendors={vendors} createBidPackage={createBidPackage} openBidPackage={openBidPackage} processing={processing} />}
      {activeStep === 8 && <Step7Leveling caseData={caseData} equalizeBids={equalizeBids} awardBid={awardBid} processing={processing} />}
      {activeStep === 9 && <Step8Final caseData={caseData} computeScenarios={computeScenarios} historicalProjects={historicalProjects} setHistoryPopup={setHistoryPopup} updateItemNotes={updateItemNotes} processing={processing} caseId={caseId} />}

      {/* Historical popup */}
      {historyPopup && <HistoricalDialog data={historyPopup} onClose={() => setHistoryPopup(null)} />}

      {/* Change Log Dialog */}
      {changeLogOpen && <ChangeLogDialog caseId={caseId} onClose={() => setChangeLogOpen(false)} />}

      {/* Decision Candidates Dialog */}
      {candidatesOpen && (
        <CandidatesDialog
          caseData={caseData}
          extractDecisions={extractDecisions}
          tagFile={tagFile}
          decideCandidate={decideCandidate}
          processing={processing}
          onClose={() => setCandidatesOpen(false)}
        />
      )}

      {/* Revisions Dialog */}
      {revisionsOpen && <RevisionsDialog caseId={caseId} onClose={() => setRevisionsOpen(false)} />}
    </div>
  );
}

/* =================== STEP 0: SETUP =================== */
function Step0Setup({ caseData, uploadFiles, deleteFile, uploading, dragActive, setDragActive, analyzeScope, processing }) {
  return (
    <div className="space-y-6">
      <div className="swiss-card">
        <h2 className="text-lg font-heading font-semibold mb-4">Project Documents</h2>
        <div
          className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${dragActive ? "border-primary bg-primary/5" : "border-border"}`}
          onDragEnter={(e) => { e.preventDefault(); setDragActive(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDragActive(false); }}
          onDragOver={(e) => { e.preventDefault(); }}
          onDrop={(e) => { e.preventDefault(); setDragActive(false); if (e.dataTransfer.files?.length > 0) uploadFiles(e.dataTransfer.files); }}
        >
          <Upload className="w-10 h-10 mx-auto mb-3 text-muted-foreground" />
          <p className="text-sm text-muted-foreground mb-3">Drop files here or click to upload</p>
          <input id="file-input" type="file" multiple onChange={(e) => e.target.files?.length > 0 && uploadFiles(e.target.files)} className="hidden" />
          <Button variant="outline" onClick={() => document.getElementById("file-input").click()} disabled={uploading} data-testid="upload-btn">
            {uploading ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Uploading...</> : <><Upload className="w-4 h-4 mr-2" />Choose Files</>}
          </Button>
        </div>

        {caseData.files?.length > 0 && (
          <div className="mt-4 space-y-2">
            {caseData.files.map((f) => (
              <div key={f.id} className="flex items-center justify-between p-2 bg-muted rounded" data-testid={`file-${f.id}`}>
                <div className="flex items-center gap-2 min-w-0">
                  <FileIcon name={f.original_name} />
                  <span className="text-sm truncate">{f.original_name}</span>
                </div>
                <button onClick={() => deleteFile(f.id)} className="text-destructive hover:text-destructive/80">
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <LoadingButton onClick={analyzeScope} loading={processing} className="gap-2" data-testid="analyze-scope-btn">
          <Sparkles className="w-4 h-4" /> Run AI Scope Extraction
        </LoadingButton>
      </div>
    </div>
  );
}

/* =================== STEP 1: SCOPE (table view) =================== */
function Step1Scope({ caseData, updateScopeItem, addScopeItem, deleteScopeItem, revalidateScope, analyzeScope, processing }) {
  const items = caseData.scope_items || [];
  const [filterTrade, setFilterTrade] = useState("all");
  const [filterConf, setFilterConf] = useState("all");
  const [editing, setEditing] = useState(null); // item.id
  const [draft, setDraft] = useState({});
  const [adding, setAdding] = useState(false);
  const [newItem, setNewItem] = useState({ trade: "general", room: "", description: "", quantity: 1, unit: "LS", premise: "", source_doc: "Manual entry" });

  if (items.length === 0) {
    return (
      <div className="swiss-card text-center py-12">
        <ClipboardList className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
        <p className="text-muted-foreground mb-4">No scope items yet</p>
        <LoadingButton onClick={analyzeScope} loading={processing} className="gap-2"><Sparkles className="w-4 h-4" /> Extract Scope from Documents</LoadingButton>
      </div>
    );
  }

  // Filters
  const trades = Array.from(new Set(items.map(i => i.trade || "general"))).sort();
  const filtered = items.filter(it => {
    if (filterTrade !== "all" && (it.trade || "general") !== filterTrade) return false;
    if (filterConf !== "all" && (it.confidence || "medium") !== filterConf) return false;
    return true;
  });

  const startEdit = (it) => { setEditing(it.id); setDraft({ ...it }); };
  const saveEdit = async () => {
    await updateScopeItem(editing, {
      description: draft.description,
      quantity: parseFloat(draft.quantity) || 0,
      unit: draft.unit,
      room: draft.room,
      premise: draft.premise,
      trade: draft.trade,
    });
    setEditing(null);
  };

  const confBadge = (c) => {
    const colors = { high: "bg-green-100 text-green-700", medium: "bg-amber-100 text-amber-700", low: "bg-red-100 text-red-700", user: "bg-blue-100 text-blue-700" };
    return <Badge className={colors[c] || "bg-gray-100 text-gray-700"} variant="outline">{c || "medium"}</Badge>;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-muted-foreground">{filtered.length} / {items.length} items · {caseData.scope_summary}</p>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" onClick={() => setAdding(!adding)} className="gap-1" data-testid="add-scope-btn"><Plus className="w-3 h-3" /> Add</Button>
          <LoadingButton onClick={revalidateScope} loading={processing} variant="outline" className="gap-2" data-testid="revalidate-scope-btn">
            <RefreshCw className="w-4 h-4" /> Revalidate with AI
          </LoadingButton>
        </div>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        <Label className="text-xs">Discipline:</Label>
        <Select value={filterTrade} onValueChange={setFilterTrade}>
          <SelectTrigger className="w-44 h-8" data-testid="filter-trade"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            {trades.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}
          </SelectContent>
        </Select>
        <Label className="text-xs ml-2">Confidence:</Label>
        <Select value={filterConf} onValueChange={setFilterConf}>
          <SelectTrigger className="w-32 h-8" data-testid="filter-conf"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="high">High</SelectItem>
            <SelectItem value="medium">Medium</SelectItem>
            <SelectItem value="low">Low</SelectItem>
            <SelectItem value="user">User</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {caseData.missing_inputs?.length > 0 && (
        <div className="swiss-card border-amber-200 bg-amber-50">
          <div className="flex items-start gap-2">
            <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5" />
            <div>
              <p className="font-medium text-amber-900">Missing inputs:</p>
              <ul className="list-disc list-inside text-sm text-amber-800">
                {caseData.missing_inputs.map((m, i) => <li key={i}>{m}</li>)}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Add new row */}
      {adding && (
        <div className="swiss-card bg-muted/30">
          <h3 className="font-heading font-semibold mb-2">New Item</h3>
          <div className="grid grid-cols-1 md:grid-cols-7 gap-2">
            <Select value={newItem.trade} onValueChange={(v) => setNewItem({ ...newItem, trade: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{TRADES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Input placeholder="Room" value={newItem.room} onChange={e => setNewItem({ ...newItem, room: e.target.value })} />
            <Input placeholder="Description *" value={newItem.description} onChange={e => setNewItem({ ...newItem, description: e.target.value })} className="md:col-span-2" />
            <Input type="number" placeholder="Qty" value={newItem.quantity} onChange={e => setNewItem({ ...newItem, quantity: parseFloat(e.target.value) || 0 })} />
            <Input placeholder="Unit" value={newItem.unit} onChange={e => setNewItem({ ...newItem, unit: e.target.value })} />
            <div className="flex gap-1">
              <Button size="sm" onClick={async () => { if (!newItem.description) return toast.error("Description required"); await addScopeItem(newItem); setAdding(false); setNewItem({ trade: "general", room: "", description: "", quantity: 1, unit: "LS", premise: "", source_doc: "Manual entry" }); }}>Add</Button>
              <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>X</Button>
            </div>
          </div>
          <Textarea placeholder="Premise" value={newItem.premise} onChange={e => setNewItem({ ...newItem, premise: e.target.value })} rows={2} className="mt-2" />
        </div>
      )}

      {/* TABLE */}
      <div className="swiss-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left">
              <th className="py-2 pr-2 font-medium text-muted-foreground w-8"></th>
              <th className="py-2 pr-2 font-medium text-muted-foreground">Room</th>
              <th className="py-2 pr-2 font-medium text-muted-foreground">Discipline</th>
              <th className="py-2 pr-2 font-medium text-muted-foreground min-w-[220px]">Item</th>
              <th className="py-2 pr-2 font-medium text-muted-foreground">Source Doc</th>
              <th className="py-2 pr-2 font-medium text-muted-foreground min-w-[180px]">Source Quote</th>
              <th className="py-2 pr-2 font-medium text-muted-foreground min-w-[180px]">Premise</th>
              <th className="py-2 pr-2 font-medium text-muted-foreground text-right">Qty</th>
              <th className="py-2 pr-2 font-medium text-muted-foreground">Unit</th>
              <th className="py-2 pr-2 font-medium text-muted-foreground">Conf.</th>
              <th className="py-2 pr-2 font-medium text-muted-foreground">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((it) => {
              const isEditing = editing === it.id;
              return (
                <tr key={it.id} className={`border-b ${!it.selected ? "opacity-40" : ""}`} data-testid={`scope-row-${it.id}`}>
                  <td className="py-2 pr-2">
                    <Checkbox checked={it.selected} onCheckedChange={(c) => updateScopeItem(it.id, { selected: !!c })} />
                  </td>
                  <td className="py-2 pr-2">
                    {isEditing ? <Input value={draft.room || ""} onChange={e => setDraft({ ...draft, room: e.target.value })} className="h-7 text-xs" /> : (it.room || "—")}
                  </td>
                  <td className="py-2 pr-2 capitalize">
                    {isEditing ? (
                      <Select value={draft.trade} onValueChange={(v) => setDraft({ ...draft, trade: v })}>
                        <SelectTrigger className="h-7 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent>{TRADES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                      </Select>
                    ) : it.trade}
                  </td>
                  <td className="py-2 pr-2">
                    {isEditing ? <Input value={draft.description || ""} onChange={e => setDraft({ ...draft, description: e.target.value })} className="h-7 text-xs" data-testid={`edit-desc-${it.id}`} /> : it.description}
                  </td>
                  <td className="py-2 pr-2 text-xs text-muted-foreground">{it.source_doc || "—"}</td>
                  <td className="py-2 pr-2 text-xs text-muted-foreground italic">{it.source_quote ? `"${it.source_quote}"` : "—"}</td>
                  <td className="py-2 pr-2 text-xs">
                    {isEditing ? <Textarea value={draft.premise || ""} onChange={e => setDraft({ ...draft, premise: e.target.value })} rows={2} className="text-xs" /> : (it.premise || <span className="text-muted-foreground">—</span>)}
                  </td>
                  <td className="py-2 pr-2 text-right font-mono">
                    {isEditing ? <Input type="number" value={draft.quantity || 0} onChange={e => setDraft({ ...draft, quantity: parseFloat(e.target.value) || 0 })} className="h-7 text-xs text-right w-20" data-testid={`edit-qty-${it.id}`} /> : it.quantity}
                  </td>
                  <td className="py-2 pr-2 text-xs">
                    {isEditing ? <Input value={draft.unit || ""} onChange={e => setDraft({ ...draft, unit: e.target.value })} className="h-7 text-xs w-16" /> : it.unit}
                  </td>
                  <td className="py-2 pr-2">{confBadge(it.user_added ? "user" : it.confidence)}</td>
                  <td className="py-2 pr-2">
                    {isEditing ? (
                      <div className="flex gap-1">
                        <Button size="sm" onClick={saveEdit} data-testid={`save-scope-${it.id}`}>Save</Button>
                        <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>X</Button>
                      </div>
                    ) : (
                      <div className="flex gap-1">
                        <Button size="sm" variant="ghost" onClick={() => startEdit(it)} data-testid={`edit-scope-${it.id}`}><Edit3 className="w-3 h-3" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => deleteScopeItem(it.id)} className="text-destructive"><Trash2 className="w-3 h-3" /></Button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* =================== STEP 2: HIDDEN WORK =================== */
function Step2HiddenWork({ caseData, detectHiddenWork, updateHiddenWork, processing }) {
  const items = caseData.hidden_work_items || [];
  if (items.length === 0) {
    return (
      <div className="swiss-card text-center py-12">
        <AlertTriangle className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
        <p className="text-muted-foreground mb-4">No hidden work detected yet</p>
        <LoadingButton onClick={detectHiddenWork} loading={processing} className="gap-2"><Sparkles className="w-4 h-4" /> Detect Hidden Work</LoadingButton>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{items.length} implied items — effort only (pricing later)</p>
        <LoadingButton onClick={detectHiddenWork} loading={processing} variant="outline" className="gap-2"><RefreshCw className="w-4 h-4" /> Re-detect</LoadingButton>
      </div>
      {items.map((it) => <HiddenWorkRow key={it.id} item={it} update={updateHiddenWork} />)}
    </div>
  );
}

function HiddenWorkRow({ item, update }) {
  const [draft, setDraft] = useState(item);
  useEffect(() => setDraft(item), [item]);
  const save = (patch) => { update(item.id, patch); };
  return (
    <div className={`swiss-card ${item.accepted ? "" : "opacity-50"}`} data-testid={`hidden-${item.id}`}>
      <div className="flex items-start gap-3">
        <Checkbox checked={item.accepted} onCheckedChange={(c) => save({ accepted: !!c })} className="mt-1" />
        <div className="flex-1">
          <div className="flex items-center gap-2 flex-wrap mb-2">
            <Badge variant="outline">{item.trade}</Badge>
            {item.room && <Badge variant="outline">{item.room}</Badge>}
            <Badge className={item.priority === "required" || item.priority === "critical" ? "bg-red-100 text-red-700" : "bg-blue-100 text-blue-700"}>{item.priority}</Badge>
          </div>
          <p className="font-medium">{item.description}</p>
          {item.reason && (
            <div className="mt-2 text-sm text-muted-foreground border-l-2 border-primary/40 pl-2">
              <strong>Why:</strong> {item.reason}
            </div>
          )}
          <div className="mt-3 grid grid-cols-1 md:grid-cols-3 gap-2 items-center">
            <Select value={draft.effort_type || "man-hour"} onValueChange={(v) => { setDraft({ ...draft, effort_type: v }); save({ effort_type: v }); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{EFFORT_TYPES.map(t => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
            </Select>
            <Input type="number" value={draft.effort_quantity || 0}
              onChange={(e) => setDraft({ ...draft, effort_quantity: parseFloat(e.target.value) || 0 })}
              onBlur={(e) => save({ effort_quantity: parseFloat(e.target.value) || 0 })}
              data-testid={`hidden-effort-qty-${item.id}`} />
            <Select value={draft.effort_unit || "HR"} onValueChange={(v) => { setDraft({ ...draft, effort_unit: v }); save({ effort_unit: v }); }}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>{EFFORT_UNITS.map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>
    </div>
  );
}

/* =================== STEP 3: COMPLIANCE =================== */
function Step3Compliance({ caseData, checkCompliance, updateCompliance, processing }) {
  const checks = caseData.compliance_checks || [];
  if (checks.length === 0) {
    return (
      <div className="swiss-card text-center py-12">
        <Shield className="w-12 h-12 mx-auto mb-3 text-muted-foreground" />
        <p className="text-muted-foreground mb-4">No compliance checks yet</p>
        <LoadingButton onClick={checkCompliance} loading={processing} className="gap-2"><Sparkles className="w-4 h-4" /> Run Compliance Check</LoadingButton>
      </div>
    );
  }
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Score: <strong>{caseData.compliance_score || 0}%</strong> · {checks.length} checks</p>
        <LoadingButton onClick={checkCompliance} loading={processing} variant="outline" className="gap-2"><RefreshCw className="w-4 h-4" /> Re-run</LoadingButton>
      </div>
      {checks.map((c) => (
        <div key={c.id} className="swiss-card" data-testid={`compliance-${c.id}`}>
          <div className="flex items-start justify-between gap-2 mb-2">
            <div>
              <Badge className={c.priority === "critical" ? "bg-red-100 text-red-700" : c.priority === "high" ? "bg-orange-100 text-orange-700" : "bg-blue-100 text-blue-700"}>{c.priority}</Badge>
              <Badge variant="outline" className="ml-2">{c.status}</Badge>
            </div>
            <div className="flex gap-1">
              <Button size="sm" variant={c.accepted === true ? "default" : "outline"} onClick={() => updateCompliance(c.id, true, "")} data-testid={`accept-${c.id}`}><CheckCircle2 className="w-4 h-4" /></Button>
              <Button size="sm" variant={c.accepted === false ? "destructive" : "outline"} onClick={() => updateCompliance(c.id, false, "")}><X className="w-4 h-4" /></Button>
            </div>
          </div>
          <p className="font-medium mb-1">Required: {c.required_check}</p>
          <p className="text-sm text-muted-foreground mb-2"><strong>Client request:</strong> {c.client_request}</p>
          <div className="text-sm border-l-2 border-primary/40 pl-2 space-y-1">
            <p><strong>Norm:</strong> {c.norm_reference}</p>
            <p><strong>Why:</strong> {c.explanation}</p>
            <p><strong>Recommendation:</strong> {c.recommendation}</p>
          </div>
        </div>
      ))}
    </div>
  );
}

/* =================== STEP 4: SCOPE CLOSING (new) =================== */
function Step4ScopeClosing({ caseId, caseData, fetchCase }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [newItem, setNewItem] = useState({ text: "", kind: "premise" });
  const [adding, setAdding] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/cases/${caseId}/scope-closing`); setData(r.data); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const togglePremise = async (premiseId, selected) => {
    const fd = new FormData(); fd.append("selected", selected);
    try { await axios.put(`${API}/cases/${caseId}/scope-closing/premise/${premiseId}`, fd); load(); }
    catch { toast.error("Save failed"); }
  };

  const addManual = async () => {
    if (!newItem.text.trim()) return toast.error("Text required");
    try { await axios.post(`${API}/cases/${caseId}/scope-closing/manual`, { ...newItem, source: "manual", selected: true }); toast.success("Added"); setNewItem({ text: "", kind: "premise" }); setAdding(false); load(); }
    catch { toast.error("Add failed"); }
  };

  const removeManual = async (id) => {
    try { await axios.delete(`${API}/cases/${caseId}/scope-closing/manual/${id}`); load(); }
    catch { toast.error("Delete failed"); }
  };

  const lockScope = async () => {
    const label = prompt("Revision label (e.g. R1):", "R1");
    if (!label) return;
    const fd = new FormData(); fd.append("label", label);
    try { await axios.post(`${API}/cases/${caseId}/lock-scope`, fd); toast.success(`Scope locked as ${label}`); fetchCase(); }
    catch { toast.error("Lock failed"); }
  };

  const unlockScope = async () => {
    try { await axios.post(`${API}/cases/${caseId}/unlock-scope`); toast.success("Scope reopened"); fetchCase(); }
    catch { toast.error("Failed"); }
  };

  if (loading || !data) return <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const sourceBadge = (s) => {
    const map = { scope: "bg-blue-100 text-blue-700", hidden: "bg-amber-100 text-amber-700", compliance: "bg-purple-100 text-purple-700", manual: "bg-green-100 text-green-700" };
    return <Badge className={map[s] || "bg-gray-100 text-gray-700"} variant="outline">{s}</Badge>;
  };

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="kpi-card"><div className="kpi-label">Scope items</div><span className="kpi-value">{data.total_items}</span></div>
        <div className="kpi-card"><div className="kpi-label">Hidden work</div><span className="kpi-value">{data.total_hidden}</span></div>
        <div className="kpi-card"><div className="kpi-label">Compliance gaps</div><span className="kpi-value">{data.total_compliance}</span></div>
        <div className="kpi-card"><div className="kpi-label">Missing inputs</div><span className="kpi-value">{data.missing_inputs?.length || 0}</span></div>
      </div>

      {/* Discipline × Room matrix */}
      <div className="swiss-card">
        <h3 className="font-heading font-semibold mb-3">Story by Discipline × Room</h3>
        {Object.keys(data.matrix).length === 0 ? (
          <p className="text-sm text-muted-foreground">No items yet.</p>
        ) : (
          <div className="space-y-3">
            {Object.entries(data.matrix).map(([trade, rooms]) => (
              <div key={trade} className="border rounded">
                <div className="p-2 bg-muted/30 font-medium capitalize">{trade}</div>
                <div className="p-2 space-y-2">
                  {Object.entries(rooms).map(([room, list]) => (
                    <div key={room} className="border-l-2 border-primary/30 pl-2">
                      <div className="text-xs font-semibold text-muted-foreground mb-1">{room}</div>
                      <ul className="text-sm space-y-1">
                        {list.map(it => (
                          <li key={it.id} className="flex items-center gap-2" data-testid={`closing-item-${it.id}`}>
                            {sourceBadge(it.source)}
                            <span className="flex-1">{it.description}</span>
                            <span className="font-mono text-xs text-muted-foreground">{it.quantity} {it.unit}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Premises & exclusions */}
      <div className="swiss-card">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-heading font-semibold">Premises & Exclusions</h3>
          <Button size="sm" variant="outline" onClick={() => setAdding(!adding)} className="gap-1" data-testid="add-premise-btn"><Plus className="w-3 h-3" /> Add Manual</Button>
        </div>
        {adding && (
          <div className="border rounded p-3 mb-3 bg-muted/30 space-y-2">
            <div className="flex gap-2">
              <Select value={newItem.kind} onValueChange={(v) => setNewItem({ ...newItem, kind: v })}>
                <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="premise">Premise</SelectItem><SelectItem value="exclusion">Exclusion</SelectItem></SelectContent>
              </Select>
              <Input placeholder="Text..." value={newItem.text} onChange={(e) => setNewItem({ ...newItem, text: e.target.value })} className="flex-1" data-testid="premise-text-input" />
              <Button size="sm" onClick={addManual} data-testid="save-premise-btn">Save</Button>
              <Button size="sm" variant="ghost" onClick={() => setAdding(false)}>Cancel</Button>
            </div>
          </div>
        )}

        <div className="space-y-1">
          {data.aggregated_premises.map(p => (
            <label key={p.id} className="flex items-center gap-2 text-sm border-b py-1" data-testid={`premise-${p.id}`}>
              <Checkbox checked={p.selected} onCheckedChange={(c) => togglePremise(p.id, !!c)} />
              {sourceBadge(p.source)}
              <span className={`flex-1 ${p.selected ? "" : "line-through text-muted-foreground"}`}>{p.text}</span>
            </label>
          ))}
          {(data.manual_items || []).map(p => (
            <div key={p.id} className="flex items-center gap-2 text-sm border-b py-1 bg-green-50/40 px-1 rounded" data-testid={`manual-${p.id}`}>
              {sourceBadge("manual")}
              <Badge variant="outline" className="text-xs">{p.kind}</Badge>
              <span className="flex-1">{p.text}</span>
              <Button size="sm" variant="ghost" onClick={() => removeManual(p.id)} className="text-destructive p-1 h-7"><Trash2 className="w-3 h-3" /></Button>
            </div>
          ))}
        </div>
      </div>

      {/* Lock / Unlock */}
      <div className="flex justify-end gap-2">
        {caseData.scope_locked ? (
          <>
            <Badge className="bg-green-100 text-green-700 self-center">Scope Locked</Badge>
            <Button variant="outline" onClick={unlockScope} data-testid="unlock-scope-btn">Reopen Scope</Button>
          </>
        ) : (
          <Button onClick={lockScope} className="gap-2" data-testid="lock-scope-btn"><BadgeCheck className="w-4 h-4" /> Lock Scope (creates revision)</Button>
        )}
      </div>
    </div>
  );
}

/* =================== STEP 5 (was 4): COST CODES =================== */
function Step4CostCodes({ caseData, mapCostCodes, updateItemCodes, wbsTree, processing }) {
  const items = caseData.scope_items || [];
  const codeOptions = wbsTree.map(n => ({ value: n.code, label: `${n.code} — ${n.description}`, level: n.level }));
  const projectMap = caseData.project_code_mapping || [];
  const mapDict = Object.fromEntries(projectMap.map(m => [m.cost_code, m.revenue_code]));
  const [expanded, setExpanded] = useState({});
  const [allocatingId, setAllocatingId] = useState(null);
  const [allocCode, setAllocCode] = useState("");

  // Roots
  const roots = wbsTree.filter(n => !n.parent);
  // Items by cost_code
  const itemsByCode = {};
  items.forEach(it => {
    const c = it.cost_code;
    if (!c) return;
    if (!itemsByCode[c]) itemsByCode[c] = [];
    itemsByCode[c].push(it);
  });

  // unassigned
  const unassigned = items.filter(it => !it.cost_code);

  const allocate = async (itemId, code) => {
    const fd = new FormData(); fd.append("item_id", itemId); fd.append("cost_code", code);
    try { await axios.post(`${API}/cases/${caseData.id}/allocate-item`, fd); toast.success("Allocated"); setAllocatingId(null); setAllocCode(""); window.location.reload(); }
    catch { toast.error("Allocation failed"); }
  };

  const saveProjectMapping = async (newMap) => {
    try { await axios.put(`${API}/cases/${caseData.id}/project-code-mapping`, newMap); toast.success("Project mapping saved"); window.location.reload(); }
    catch { toast.error("Save failed"); }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{items.filter(i => i.cost_code).length} / {items.length} items allocated</p>
        <LoadingButton onClick={mapCostCodes} loading={processing} variant="outline" className="gap-2"><Sparkles className="w-4 h-4" /> AI Suggest (best effort)</LoadingButton>
      </div>

      {/* Project-level cost <-> revenue mapping */}
      <ProjectMappingPanel projectMap={projectMap} mapDict={mapDict} wbsTree={wbsTree} codeOptions={codeOptions} save={saveProjectMapping} />

      {/* Unassigned items */}
      {unassigned.length > 0 && (
        <div className="swiss-card border-amber-300">
          <h3 className="font-heading font-semibold mb-3 flex items-center gap-2 text-amber-700">
            <AlertCircle className="w-4 h-4" /> Unassigned items ({unassigned.length})
          </h3>
          <div className="space-y-2">
            {unassigned.map(it => (
              <div key={it.id} className="flex items-center gap-2 border-b py-2" data-testid={`unassigned-${it.id}`}>
                <span className="flex-1 text-sm">{it.room ? `${it.room}: ` : ""}{it.description}</span>
                {allocatingId === it.id ? (
                  <>
                    <Select value={allocCode} onValueChange={setAllocCode}>
                      <SelectTrigger className="w-72"><SelectValue placeholder="Choose cost code" /></SelectTrigger>
                      <SelectContent className="max-h-96">{codeOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
                    </Select>
                    <Button size="sm" disabled={!allocCode} onClick={() => allocate(it.id, allocCode)} data-testid={`alloc-confirm-${it.id}`}>Allocate</Button>
                    <Button size="sm" variant="ghost" onClick={() => { setAllocatingId(null); setAllocCode(""); }}>Cancel</Button>
                  </>
                ) : (
                  <Button size="sm" onClick={() => { setAllocatingId(it.id); setAllocCode(""); }} data-testid={`alloc-btn-${it.id}`}>Allocate</Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Tree view of allocated items */}
      <div className="swiss-card">
        <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><ListTree className="w-4 h-4" /> Allocated by Division</h3>
        {roots.map(root => {
          const isOpen = expanded[root.code];
          const inRoot = items.filter(it => {
            if (!it.cost_code) return false;
            let cur = wbsTree.find(n => n.code === it.cost_code);
            let r = it.cost_code;
            while (cur && cur.parent) { r = cur.parent; cur = wbsTree.find(n => n.code === cur.parent); }
            return r === root.code;
          });
          return (
            <div key={root.code} className="border-b last:border-b-0 py-2">
              <button onClick={() => setExpanded({ ...expanded, [root.code]: !isOpen })} className="w-full flex items-center gap-2 text-left">
                {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                <span className="font-mono text-xs text-muted-foreground">{root.code}</span>
                <span className="font-medium">{root.description}</span>
                {inRoot.length > 0 && <Badge variant="secondary" className="ml-auto">{inRoot.length}</Badge>}
              </button>
              {isOpen && inRoot.length > 0 && (
                <div className="mt-2 ml-6 space-y-1">
                  {inRoot.map(it => (
                    <div key={it.id} className="text-sm py-1 flex items-center justify-between gap-2 border-b last:border-b-0">
                      <span className="flex-1 truncate">{it.room ? `${it.room}: ` : ""}{it.description}</span>
                      <Badge variant="outline" className="font-mono text-xs">CC {it.cost_code}</Badge>
                      <Badge variant="outline" className="font-mono text-xs">RC {it.revenue_code || it.cost_code}</Badge>
                      <Button size="sm" variant="ghost" onClick={() => { setAllocatingId(it.id); setAllocCode(it.cost_code); }}><Edit3 className="w-3 h-3" /></Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Re-allocate dropdown when editing existing */}
      {allocatingId && items.find(i => i.id === allocatingId)?.cost_code && (
        <div className="swiss-card bg-muted/30">
          <p className="text-sm font-medium mb-2">Re-allocate {items.find(i => i.id === allocatingId)?.description}</p>
          <div className="flex gap-2">
            <Select value={allocCode} onValueChange={setAllocCode}>
              <SelectTrigger><SelectValue placeholder="Choose cost code" /></SelectTrigger>
              <SelectContent className="max-h-96">{codeOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
            <Button onClick={() => allocate(allocatingId, allocCode)} disabled={!allocCode}>Save</Button>
            <Button variant="ghost" onClick={() => { setAllocatingId(null); setAllocCode(""); }}>Cancel</Button>
          </div>
        </div>
      )}
    </div>
  );
}

function ProjectMappingPanel({ projectMap, mapDict, wbsTree, codeOptions, save }) {
  const [open, setOpen] = useState(false);
  const [rows, setRows] = useState(projectMap.length ? projectMap : []);
  const [newCost, setNewCost] = useState("");
  const [newRev, setNewRev] = useState("");

  return (
    <div className="swiss-card">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 text-left">
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <h3 className="font-heading font-semibold flex-1">Project Mapping (Cost ↔ Revenue)</h3>
        <Badge variant="secondary">{projectMap.length} overrides</Badge>
      </button>
      {open && (
        <div className="mt-3 space-y-2">
          <p className="text-xs text-muted-foreground">By default, revenue code = cost code. Define exceptions for this project here.</p>
          {rows.map((m, i) => (
            <div key={i} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-center" data-testid={`mapping-row-${i}`}>
              <Select value={m.cost_code} onValueChange={(v) => { const a = [...rows]; a[i] = { ...m, cost_code: v }; setRows(a); }}>
                <SelectTrigger className="md:col-span-2"><SelectValue placeholder="Cost code" /></SelectTrigger>
                <SelectContent className="max-h-80">{codeOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              <span className="text-center text-muted-foreground">↔</span>
              <Select value={m.revenue_code} onValueChange={(v) => { const a = [...rows]; a[i] = { ...m, revenue_code: v }; setRows(a); }}>
                <SelectTrigger className="md:col-span-1"><SelectValue placeholder="Revenue code" /></SelectTrigger>
                <SelectContent className="max-h-80">{codeOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
              </Select>
              <Button size="sm" variant="ghost" onClick={() => setRows(rows.filter((_, k) => k !== i))}><Trash2 className="w-3 h-3" /></Button>
            </div>
          ))}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-center border-t pt-2">
            <Select value={newCost} onValueChange={setNewCost}>
              <SelectTrigger className="md:col-span-2"><SelectValue placeholder="+ Cost code" /></SelectTrigger>
              <SelectContent className="max-h-80">{codeOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
            <span className="text-center text-muted-foreground">↔</span>
            <Select value={newRev} onValueChange={setNewRev}>
              <SelectTrigger><SelectValue placeholder="+ Revenue code" /></SelectTrigger>
              <SelectContent className="max-h-80">{codeOptions.map(o => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent>
            </Select>
            <Button size="sm" disabled={!newCost || !newRev} onClick={() => { setRows([...rows, { cost_code: newCost, revenue_code: newRev }]); setNewCost(""); setNewRev(""); }}>Add</Button>
          </div>
          <div className="flex justify-end pt-2">
            <Button onClick={() => save(rows)} data-testid="save-mapping-btn">Save Mapping</Button>
          </div>
        </div>
      )}
    </div>
  );
}

/* =================== STEP 5: PRICING (macro + override) =================== */
function Step5Pricing({ caseData, setMacroPricing, setPricingStrategy, overrideItemPricing, updateItemNotes, wbsTree, processing }) {
  const items = caseData.scope_items || [];
  // Get unique macro roots based on cost codes
  const macros = {};
  items.forEach(it => {
    if (!it.cost_code) return;
    let cur = wbsTree.find(n => n.code === it.cost_code);
    let root = it.cost_code;
    while (cur && cur.parent) { root = cur.parent; cur = wbsTree.find(n => n.code === cur.parent); }
    if (!macros[root]) {
      const info = wbsTree.find(n => n.code === root);
      macros[root] = { code: root, description: info?.description || root, items: [] };
    }
    macros[root].items.push(it);
  });

  // Load any existing macro pricing
  const existing = caseData.macro_pricing || [];
  const [macroDraft, setMacroDraft] = useState(() =>
    Object.values(macros).map(m => {
      const found = existing.find(e => e.cost_code === m.code);
      return found || { cost_code: m.code, contract_type: "lump_sum", fee_percent: 10, margin_percent: 15, contingency_percent: 5 };
    })
  );

  const saveMacro = async () => { await setMacroPricing(macroDraft); };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">Macro pricing (per cost code root) cascades to items. Override per item below.</p>
        <LoadingButton onClick={setPricingStrategy} loading={processing} variant="outline" className="gap-2"><Sparkles className="w-4 h-4" /> AI Suggest Methods</LoadingButton>
      </div>

      <div className="swiss-card">
        <h3 className="font-heading font-semibold mb-3">Macro Cost Code Pricing</h3>
        <div className="hidden md:grid md:grid-cols-6 gap-2 text-xs font-medium text-muted-foreground mb-1 border-b pb-1">
          <span className="md:col-span-2">Cost Code (root)</span>
          <span>Contract Type</span>
          <span>Fee %</span>
          <span>Margin %</span>
          <span>Contingency %</span>
        </div>
        <div className="space-y-2">
          {macroDraft.map((m, idx) => (
            <div key={m.cost_code} className="grid grid-cols-1 md:grid-cols-6 gap-2 items-center border-b py-2" data-testid={`macro-${m.cost_code}`}>
              <div className="text-sm md:col-span-2"><span className="font-mono text-xs text-muted-foreground">{m.cost_code}</span> {macros[m.cost_code]?.description}</div>
              <Select value={m.contract_type} onValueChange={(v) => { const arr = [...macroDraft]; arr[idx] = { ...m, contract_type: v }; setMacroDraft(arr); }}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CONTRACT_TYPES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
              <Input type="number" value={m.fee_percent} onChange={e => { const arr = [...macroDraft]; arr[idx] = { ...m, fee_percent: parseFloat(e.target.value) || 0 }; setMacroDraft(arr); }} placeholder="Fee %" />
              <Input type="number" value={m.margin_percent} onChange={e => { const arr = [...macroDraft]; arr[idx] = { ...m, margin_percent: parseFloat(e.target.value) || 0 }; setMacroDraft(arr); }} placeholder="Margin %" />
              <Input type="number" value={m.contingency_percent} onChange={e => { const arr = [...macroDraft]; arr[idx] = { ...m, contingency_percent: parseFloat(e.target.value) || 0 }; setMacroDraft(arr); }} placeholder="Contingency %" />
            </div>
          ))}
        </div>
        <div className="mt-3 flex justify-end">
          <Button onClick={saveMacro} data-testid="save-macro-btn">Save Macro Pricing</Button>
        </div>
      </div>

      <div className="swiss-card">
        <h3 className="font-heading font-semibold mb-3">Per-Item Override</h3>
        <div className="hidden md:grid md:grid-cols-5 gap-2 text-xs font-medium text-muted-foreground mb-1 border-b pb-1">
          <span className="md:col-span-2">Item</span>
          <span>Pricing Method</span>
          <span>Contract Type (override)</span>
          <span>Notes</span>
        </div>
        <div className="space-y-2">
          {items.map(it => (
            <div key={it.id} className="grid grid-cols-1 md:grid-cols-5 gap-2 items-center border-b py-2" data-testid={`pricing-item-${it.id}`}>
              <div className="text-sm md:col-span-2 truncate">{it.description}<span className="ml-2 text-xs text-muted-foreground">{it.cost_code ? `· CC ${it.cost_code}` : ""}</span></div>
              <Select value={it.pricing_method || ""} onValueChange={(v) => overrideItemPricing(it.id, { pricing_method: v })}>
                <SelectTrigger><SelectValue placeholder="Method" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="quote">Quote (vendor)</SelectItem>
                  <SelectItem value="historical">Historical</SelectItem>
                  <SelectItem value="manual">Manual</SelectItem>
                </SelectContent>
              </Select>
              <Select value={it.contract_type_override || it.inherited_contract_type || ""} onValueChange={(v) => overrideItemPricing(it.id, { contract_type: v })}>
                <SelectTrigger><SelectValue placeholder="Contract type" /></SelectTrigger>
                <SelectContent>{CONTRACT_TYPES.map(c => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
              </Select>
              <NotesIcons item={it} updateItemNotes={updateItemNotes} />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

/* =================== STEP 6: BIDDING =================== */
function Step6Bidding({ caseData, vendors, createBidPackage, openBidPackage, processing }) {
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ name: "", description: "", deadline: "", scope_item_ids: [], vendor_ids: [], visible_doc_ids: [] });
  const packages = caseData.bid_packages || [];
  const items = caseData.scope_items?.filter(s => s.selected) || [];
  const files = caseData.files || [];

  const copyLink = (token) => {
    const link = `${window.location.origin}/estimate/vendor/bid/${token}`;
    navigator.clipboard.writeText(link);
    toast.success("Link copied");
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{packages.length} bid package(s)</p>
        <Button onClick={() => setCreating(true)} className="gap-2" data-testid="new-package-btn"><Plus className="w-4 h-4" /> New Package</Button>
      </div>

      {creating && (
        <div className="swiss-card">
          <h3 className="font-heading font-semibold mb-3">New Bid Package</h3>
          <div className="space-y-3">
            <Input placeholder="Package name *" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="pkg-name-input" />
            <Textarea placeholder="Description for vendors" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} rows={3} />
            <Input type="date" value={form.deadline} onChange={e => setForm({ ...form, deadline: e.target.value })} />

            <div>
              <Label className="text-sm font-medium">Items to bid ({form.scope_item_ids.length} selected)</Label>
              <div className="max-h-48 overflow-y-auto border rounded p-2 space-y-1 mt-1">
                {items.map(it => (
                  <label key={it.id} className="flex items-center gap-2 text-sm">
                    <Checkbox checked={form.scope_item_ids.includes(it.id)} onCheckedChange={(c) => setForm({ ...form, scope_item_ids: c ? [...form.scope_item_ids, it.id] : form.scope_item_ids.filter(x => x !== it.id) })} />
                    <span className="truncate">{it.room ? `${it.room}: ` : ""}{it.description} ({it.quantity} {it.unit})</span>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <Label className="text-sm font-medium">Vendors to invite</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1 mt-1">
                {vendors.length === 0 ? <p className="text-xs text-muted-foreground">No vendors yet</p> :
                  vendors.map(v => (
                    <label key={v.id} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={form.vendor_ids.includes(v.id)} onCheckedChange={(c) => setForm({ ...form, vendor_ids: c ? [...form.vendor_ids, v.id] : form.vendor_ids.filter(x => x !== v.id) })} />
                      <span>{v.name} ({v.email})</span>
                    </label>
                  ))
                }
              </div>
              <CreateVendorInline onCreated={() => window.location.reload()} />
            </div>

            <div>
              <Label className="text-sm font-medium">Documents visible to vendor (mocked — names only)</Label>
              <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1 mt-1">
                {files.length === 0 ? <p className="text-xs text-muted-foreground">No documents</p> :
                  files.map(f => (
                    <label key={f.id} className="flex items-center gap-2 text-sm">
                      <Checkbox checked={form.visible_doc_ids.includes(f.id)} onCheckedChange={(c) => setForm({ ...form, visible_doc_ids: c ? [...form.visible_doc_ids, f.id] : form.visible_doc_ids.filter(x => x !== f.id) })} />
                      <FileIcon name={f.original_name} />
                      <span>{f.original_name}</span>
                    </label>
                  ))
                }
              </div>
            </div>

            <div className="flex gap-2 justify-end">
              <Button variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
              <Button onClick={async () => { if (!form.name) return toast.error("Name required"); await createBidPackage(form); setCreating(false); setForm({ name: "", description: "", deadline: "", scope_item_ids: [], vendor_ids: [], visible_doc_ids: [] }); }} data-testid="create-package-btn">Create</Button>
            </div>
          </div>
        </div>
      )}

      {packages.map(pkg => {
        const pkgItems = caseData.scope_items?.filter(s => (pkg.scope_item_ids || []).includes(s.id)) || [];
        return (
          <div key={pkg.id} className="swiss-card" data-testid={`package-${pkg.id}`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div>
                <h3 className="font-heading font-semibold">{pkg.name}</h3>
                <p className="text-sm text-muted-foreground">{pkgItems.length} items · {pkg.invitations?.length || 0} vendors · {pkg.visible_doc_ids?.length || 0} docs visible</p>
                {pkg.description && <p className="text-xs text-muted-foreground mt-1">{pkg.description}</p>}
              </div>
              <Badge className={pkg.status === "open" ? "bg-green-100 text-green-700" : pkg.status === "closed" ? "bg-gray-100 text-gray-700" : "bg-yellow-100 text-yellow-700"}>{pkg.status}</Badge>
            </div>

            {/* Item detail per package */}
            <details className="border-t pt-2 mb-2">
              <summary className="text-sm font-medium cursor-pointer">View items ({pkgItems.length})</summary>
              <div className="mt-2 space-y-1">
                <div className="hidden md:grid md:grid-cols-6 text-xs font-medium text-muted-foreground border-b pb-1">
                  <span>Room</span><span className="md:col-span-2">Description</span><span className="text-right">Qty</span><span>Unit</span><span>Cost Code</span>
                </div>
                {pkgItems.map(it => (
                  <div key={it.id} className="grid grid-cols-1 md:grid-cols-6 text-sm py-1 border-b last:border-b-0">
                    <span>{it.room || "—"}</span>
                    <span className="md:col-span-2 truncate">{it.description}</span>
                    <span className="text-right font-mono">{it.quantity}</span>
                    <span>{it.unit}</span>
                    <span className="font-mono text-xs">{it.cost_code || "—"}</span>
                  </div>
                ))}
              </div>
            </details>

            {pkg.status === "draft" && <Button size="sm" onClick={() => openBidPackage(pkg.id)} data-testid={`open-pkg-${pkg.id}`}>Open for Submissions</Button>}
            <div className="mt-2 space-y-1">
              {pkg.invitations?.map(inv => (
                <div key={inv.token} className="flex items-center justify-between text-sm border rounded p-2">
                  <span>{inv.vendor_name} <Badge variant="outline" className="ml-2">{inv.status}</Badge>{inv.response && <span className="ml-2 font-mono">{fmtCurrency(inv.response.total)}</span>}{inv.response?.excluded_item_ids?.length > 0 && <span className="ml-2 text-xs text-amber-600">excluded {inv.response.excluded_item_ids.length}</span>}</span>
                  <Button size="sm" variant="outline" onClick={() => copyLink(inv.token)} data-testid={`copy-link-${inv.token}`}>Copy Link</Button>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function CreateVendorInline({ onCreated }) {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState({ name: "", contact_name: "", email: "", phone: "", trades: [] });
  if (!open) return <Button size="sm" variant="ghost" className="mt-2 gap-1" onClick={() => setOpen(true)}><Plus className="w-3 h-3" /> New Vendor</Button>;
  return (
    <div className="border rounded p-2 mt-2 space-y-2">
      <Input placeholder="Company" value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} />
      <Input placeholder="Contact name" value={draft.contact_name} onChange={e => setDraft({ ...draft, contact_name: e.target.value })} />
      <Input placeholder="Email" value={draft.email} onChange={e => setDraft({ ...draft, email: e.target.value })} />
      <div className="flex gap-2">
        <Button size="sm" onClick={async () => { try { await axios.post(`${API}/vendors`, draft); toast.success("Vendor created"); onCreated(); } catch { toast.error("Failed"); } }}>Save</Button>
        <Button size="sm" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
      </div>
    </div>
  );
}

/* =================== STEP 7: LEVELING (per-item matrix) =================== */
function Step7Leveling({ caseData, equalizeBids, awardBid, processing }) {
  const packages = (caseData.bid_packages || []).filter(p => p.invitations?.some(i => i.response));
  const [projectAnalysis, setProjectAnalysis] = useState(caseData.leveling_analysis || null);
  const [runningProjectAnalysis, setRunningProjectAnalysis] = useState(false);

  const runProjectAnalysis = async () => {
    setRunningProjectAnalysis(true);
    try {
      const r = await axios.post(`${API}/cases/${caseData.id}/leveling-analysis`);
      setProjectAnalysis(r.data);
      toast.success("Project leveling analyzed");
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setRunningProjectAnalysis(false); }
  };

  if (packages.length === 0) {
    return <div className="swiss-card text-center py-12"><Award className="w-12 h-12 mx-auto mb-3 text-muted-foreground" /><p className="text-muted-foreground">No vendor responses yet</p></div>;
  }

  return (
    <div className="space-y-4">
      {/* Project-wide AI analysis */}
      <div className="swiss-card">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-heading font-semibold flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Project-Wide Leveling Analysis</h3>
          <LoadingButton onClick={runProjectAnalysis} loading={runningProjectAnalysis} variant="outline" className="gap-2" data-testid="project-analysis-btn"><Sparkles className="w-4 h-4" /> Analyze All Picks</LoadingButton>
        </div>
        {projectAnalysis ? (
          <div className="space-y-2 text-sm">
            <p><strong>Score:</strong> {projectAnalysis.score}/100</p>
            <p className="text-muted-foreground">{projectAnalysis.summary}</p>
            {projectAnalysis.risks?.length > 0 && <div><strong className="text-amber-700">Risks:</strong> <ul className="list-disc list-inside text-xs">{projectAnalysis.risks.map((r, i) => <li key={i}>{r}</li>)}</ul></div>}
            {projectAnalysis.recommendations?.length > 0 && <div><strong className="text-green-700">Recommendations:</strong> <ul className="list-disc list-inside text-xs">{projectAnalysis.recommendations.map((r, i) => <li key={i}>{r}</li>)}</ul></div>}
          </div>
        ) : <p className="text-sm text-muted-foreground">Set per-item vendor picks below, then run project-wide analysis.</p>}
      </div>

      {packages.map(pkg => <LevelingPackage key={pkg.id} pkg={pkg} caseData={caseData} equalizeBids={equalizeBids} processing={processing} />)}
    </div>
  );
}

function LevelingPackage({ pkg, caseData, equalizeBids, processing }) {
  const respondingInvs = pkg.invitations.filter(i => i.response);
  const items = (caseData.scope_items || []).filter(s => (pkg.scope_item_ids || []).includes(s.id));
  const initialPicks = Object.fromEntries((pkg.leveling_picks || []).map(p => [p.item_id, p.vendor_id]));
  const [picks, setPicks] = useState(initialPicks);
  const [saving, setSaving] = useState(false);

  const getBid = (vendorId, itemId) => {
    const inv = pkg.invitations.find(i => i.vendor_id === vendorId);
    if (!inv?.response) return null;
    const bi = (inv.response.items || []).find(b => b.id === itemId);
    if (!bi) return null;
    const excluded = (inv.response.excluded_item_ids || []).includes(itemId);
    return { unit: bi.unit_price, excluded };
  };

  const savePicks = async () => {
    setSaving(true);
    try {
      const payload = Object.entries(picks).filter(([, v]) => v).map(([item_id, vendor_id]) => ({ item_id, vendor_id }));
      await axios.put(`${API}/cases/${caseData.id}/bid-packages/${pkg.id}/leveling-picks`, payload);
      toast.success("Picks saved");
    } catch { toast.error("Save failed"); }
    finally { setSaving(false); }
  };

  // totals per vendor (sum of their bid for items where they're picked; if no pick, sum across all their items for comparison)
  const totalPerVendor = (vendorId) => respondingInvs.find(i => i.vendor_id === vendorId)?.response?.total || 0;

  const pickedTotal = items.reduce((sum, it) => {
    const v = picks[it.id];
    if (!v) return sum;
    const b = getBid(v, it.id);
    if (!b || b.excluded) return sum;
    return sum + (b.unit * (it.quantity || 1));
  }, 0);

  return (
    <div className="swiss-card" data-testid={`leveling-${pkg.id}`}>
      <div className="flex items-center justify-between mb-3">
        <div>
          <h3 className="font-heading font-semibold">{pkg.name}</h3>
          <p className="text-sm text-muted-foreground">{respondingInvs.length} responses · {items.length} items · picked total: <span className="font-mono">{fmtCurrency(pickedTotal)}</span></p>
        </div>
        <LoadingButton onClick={() => equalizeBids(pkg.id)} loading={processing} variant="outline" className="gap-2" data-testid={`ai-level-${pkg.id}`}><Sparkles className="w-4 h-4" /> AI Suggest Picks</LoadingButton>
      </div>

      {/* AI ranked (overall) */}
      {pkg.equalization?.ranked && (
        <details className="border rounded p-2 mb-3">
          <summary className="text-sm font-medium cursor-pointer">AI overall ranking</summary>
          <div className="mt-2 space-y-1 text-sm">
            {pkg.equalization.ranked.map((r, i) => (
              <div key={r.vendor_id} className="border rounded p-2">
                <div className="flex items-center justify-between">
                  <span>#{i + 1} {r.vendor_name} <Badge className="ml-2">{r.score?.toFixed?.(1)}</Badge></span>
                  <span className="font-mono">{fmtCurrency(totalPerVendor(r.vendor_id))}</span>
                </div>
                <p className="text-xs text-muted-foreground mt-1">{r.rationale}</p>
              </div>
            ))}
          </div>
        </details>
      )}

      {/* Matrix */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b">
              <th className="text-left py-2 pr-2 font-medium">Item</th>
              <th className="text-right py-2 pr-2 font-medium">Qty</th>
              {respondingInvs.map(inv => (
                <th key={inv.vendor_id} className="text-right py-2 pr-2 font-medium">{inv.vendor_name}</th>
              ))}
              <th className="text-left py-2 pl-2 font-medium">Pick</th>
            </tr>
          </thead>
          <tbody>
            {items.map(it => (
              <tr key={it.id} className="border-b" data-testid={`level-row-${it.id}`}>
                <td className="py-2 pr-2"><span className="text-xs text-muted-foreground">{it.room ? `${it.room}: ` : ""}</span>{it.description}</td>
                <td className="py-2 pr-2 text-right font-mono text-xs">{it.quantity} {it.unit}</td>
                {respondingInvs.map(inv => {
                  const b = getBid(inv.vendor_id, it.id);
                  const isPicked = picks[it.id] === inv.vendor_id;
                  return (
                    <td key={inv.vendor_id} className={`py-2 pr-2 text-right font-mono ${isPicked ? "bg-green-50 text-green-700 font-semibold" : ""} ${b?.excluded ? "text-muted-foreground italic" : ""}`}>
                      {b ? (b.excluded ? "excluded" : fmtCurrency(b.unit)) : "—"}
                    </td>
                  );
                })}
                <td className="py-2 pl-2">
                  <Select value={picks[it.id] || ""} onValueChange={(v) => setPicks({ ...picks, [it.id]: v })}>
                    <SelectTrigger className="w-44" data-testid={`pick-${it.id}`}><SelectValue placeholder="Choose vendor" /></SelectTrigger>
                    <SelectContent>
                      {respondingInvs.map(inv => {
                        const b = getBid(inv.vendor_id, it.id);
                        if (!b || b.excluded) return null;
                        return <SelectItem key={inv.vendor_id} value={inv.vendor_id}>{inv.vendor_name} — {fmtCurrency(b.unit)}</SelectItem>;
                      })}
                    </SelectContent>
                  </Select>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="mt-3 flex justify-end gap-2">
        <Button onClick={savePicks} disabled={saving} data-testid={`save-picks-${pkg.id}`}>{saving ? "Saving..." : "Save Item-Level Picks"}</Button>
      </div>
    </div>
  );
}

/* =================== STEP 8: FINAL (single table: Price | Format | Cont% | Override | Final) =================== */
function Step8Final({ caseData, computeScenarios, historicalProjects, setHistoryPopup, updateItemNotes, processing, caseId }) {
  const [rationale, setRationale] = useState("");
  const [overrides, setOverrides] = useState({});
  const [itemContingency, setItemContingency] = useState(() => {
    const seed = {};
    (caseData.scope_items || []).forEach(it => { if (it.contingency_percent != null) seed[it.id] = it.contingency_percent; });
    return seed;
  });
  const [freeQuery, setFreeQuery] = useState("");
  const [freeAnswer, setFreeAnswer] = useState("");
  const [freeLoading, setFreeLoading] = useState(false);
  const scenarios = caseData.scenarios;

  const run = () => computeScenarios(0, overrides, rationale, itemContingency);

  const runFreeAnalysis = async () => {
    if (!freeQuery.trim()) return toast.error("Type your question first");
    setFreeLoading(true);
    setFreeAnswer("");
    try {
      const fd = new FormData(); fd.append("query", freeQuery);
      const r = await axios.post(`${API}/cases/${caseId}/scenarios/free-analysis`, fd);
      setFreeAnswer(r.data.answer);
    } catch (e) { toast.error(e.response?.data?.detail || "Analysis failed"); }
    finally { setFreeLoading(false); }
  };

  const openHistoryFor = async (it) => {
    try {
      const r = await axios.get(`${API}/cases/${caseId}/item-history/${it.item_id}`);
      setHistoryPopup({ ...r.data });
    } catch { toast.error("Failed to load history"); }
  };

  // Determine contract format per item (override or inherited from macro)
  const formatLabel = (code) => CONTRACT_TYPES.find(c => c.value === code)?.label || "—";
  const itemFormat = (it) => {
    const fromItems = (caseData.scope_items || []).find(s => s.id === it.item_id);
    return fromItems?.contract_type_override || fromItems?.inherited_contract_type || "";
  };

  // Final price = override*qty if override else live calc: unit_price * qty * (1 + cont%/100)
  const finalPriceFor = (it) => {
    const ov = overrides[it.item_id];
    if (ov != null && !Number.isNaN(ov)) return ov * (it.quantity || 1);
    const cont = itemContingency[it.item_id] ?? it.contingency_percent ?? 0;
    return (it.unit_price_a || 0) * (it.quantity || 1) * (1 + cont / 100);
  };

  const grandFinal = (scenarios?.tree || []).reduce((sum, g) =>
    sum + g.items.reduce((s, it) => s + finalPriceFor(it), 0), 0);

  return (
    <div className="space-y-4">
      <div className="swiss-card">
        <h3 className="font-heading font-semibold mb-3">Scenario Rationale</h3>
        <Label>Why this price/logic? (applies to whole project)</Label>
        <Textarea value={rationale} onChange={e => setRationale(e.target.value)} rows={2} data-testid="rationale-textarea" className="mt-1" />
        <p className="text-xs text-muted-foreground mt-2">Contingency is set <strong>per item</strong> in the table below. Final price = Override × Qty (if set) or Price × (1 + Cont%).</p>
        <LoadingButton onClick={run} loading={processing} className="gap-2 mt-3" data-testid="run-scenarios-btn"><Calculator className="w-4 h-4" /> Compute / Recompute</LoadingButton>
      </div>

      {scenarios && (
        <>
          <div className="swiss-card">
            <div className="flex items-center justify-between mb-3">
              <h3 className="font-heading font-semibold flex items-center gap-2"><ListTree className="w-4 h-4" /> Final Estimate by Revenue Code</h3>
              <span className="font-mono text-xl font-bold" data-testid="final-grand-total">{fmtCurrency(grandFinal)}</span>
            </div>
            <p className="text-xs text-muted-foreground mb-2">Cada item exibe os campos lado a lado em telas grandes e empilhados em telas menores — sem scroll horizontal.</p>
            <div className="space-y-3 mt-2">
              {(scenarios.tree || []).map(group => (
                <ScenarioGroup
                  key={group.code}
                  group={group}
                  overrides={overrides}
                  setOverrides={setOverrides}
                  itemContingency={itemContingency}
                  setItemContingency={setItemContingency}
                  openHistoryFor={openHistoryFor}
                  formatLabel={formatLabel}
                  itemFormat={itemFormat}
                  finalPriceFor={finalPriceFor}
                  scopeItems={caseData.scope_items || []}
                  updateItemNotes={updateItemNotes}
                />
              ))}
            </div>
            <div className="mt-3 flex justify-end">
              <Button onClick={run} variant="outline" className="gap-2" data-testid="recompute-btn"><RefreshCw className="w-4 h-4" /> Recompute</Button>
            </div>
          </div>

          {/* Free analysis: user asks anything */}
          <div className="swiss-card">
            <h3 className="font-heading font-semibold mb-3 flex items-center gap-2"><BarChart3 className="w-4 h-4" /> Ask the AI about this estimate</h3>
            <p className="text-xs text-muted-foreground mb-2">Examples: "Qual o % do item de pisos no total?", "Compare o preço do AC com a média histórica", "Qual cost code tem maior risco de overrun?"</p>
            <div className="flex gap-2">
              <Textarea value={freeQuery} onChange={e => setFreeQuery(e.target.value)} rows={2} placeholder="Type your question..." data-testid="free-query-textarea" className="flex-1" />
              <LoadingButton onClick={runFreeAnalysis} loading={freeLoading} className="gap-2 self-end" data-testid="ask-ai-btn"><Sparkles className="w-4 h-4" /> Ask</LoadingButton>
            </div>
            {freeAnswer && (
              <div className="mt-3 border rounded p-3 bg-muted/30 text-sm whitespace-pre-wrap" data-testid="free-answer">{freeAnswer}</div>
            )}
          </div>
        </>
      )}
    </div>
  );
}

function ScenarioGroup({ group, overrides, setOverrides, itemContingency, setItemContingency, openHistoryFor, formatLabel, itemFormat, finalPriceFor, scopeItems, updateItemNotes }) {
  const [open, setOpen] = useState(true);
  const groupFinal = group.items.reduce((s, it) => s + finalPriceFor(it), 0);
  return (
    <div className="border rounded">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center gap-2 p-2 text-left bg-muted/30">
        {open ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
        <span className="font-mono text-xs text-muted-foreground">{group.code}</span>
        <span className="font-medium">{group.description}</span>
        <span className="ml-auto font-mono text-sm font-semibold">{fmtCurrency(groupFinal)}</span>
      </button>
      {open && (
        <div className="divide-y">
          {group.items.map(it => {
            const fmt = itemFormat(it);
            const final = finalPriceFor(it);
            const fullItem = (scopeItems || []).find(s => s.id === it.item_id) || { id: it.item_id };
            const basePrice = (it.unit_price_a || 0) * (it.quantity || 1);
            return (
              <div key={it.item_id} className="p-3 hover:bg-muted/20" data-testid={`final-row-${it.item_id}`}>
                {/* Top row: description + final price */}
                <div className="flex items-start justify-between gap-3 mb-2">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium break-words" title={`${it.room || ""} ${it.description || ""}`.trim()}>
                      {it.room ? <span className="text-xs text-muted-foreground mr-1">{it.room}:</span> : null}
                      {it.description}
                    </p>
                    <div className="flex items-center gap-2 mt-1 flex-wrap">
                      <Badge variant="outline" className="text-[10px] py-0">{it.pricing_method || "—"}</Badge>
                      {fmt && <Badge variant="outline" className="text-[10px] py-0">{formatLabel(fmt)}</Badge>}
                      {it.source && <span className="text-[11px] text-muted-foreground truncate max-w-[260px]" title={it.source}>{it.source}</span>}
                    </div>
                  </div>
                  <div className="text-right shrink-0">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wide">Final</p>
                    <p className="font-mono text-base font-semibold" data-testid={`final-price-${it.item_id}`}>{fmtCurrency(final)}</p>
                  </div>
                </div>

                {/* Inputs row */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 items-end">
                  <div>
                    <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Price (calc)</Label>
                    <div className="h-8 px-2 flex items-center font-mono text-xs text-muted-foreground bg-muted/30 rounded">
                      {fmtCurrency(basePrice)}
                    </div>
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Cont %</Label>
                    <Input type="number" defaultValue={itemContingency[it.item_id] ?? it.contingency_percent ?? 0}
                      onBlur={(e) => setItemContingency({ ...itemContingency, [it.item_id]: parseFloat(e.target.value) || 0 })}
                      className="h-8 text-xs text-right font-mono"
                      data-testid={`cont-${it.item_id}`} />
                  </div>
                  <div>
                    <Label className="text-[10px] text-muted-foreground uppercase tracking-wide">Override $/u</Label>
                    <Input type="number" placeholder="optional" defaultValue={overrides[it.item_id] || ""}
                      onBlur={(e) => {
                        const v = e.target.value === "" ? undefined : parseFloat(e.target.value);
                        setOverrides({ ...overrides, [it.item_id]: v });
                      }}
                      className="h-8 text-xs text-right font-mono"
                      data-testid={`override-${it.item_id}`} />
                  </div>
                  <div className="flex items-center justify-end gap-1">
                    {updateItemNotes && <NotesIcons item={fullItem} updateItemNotes={updateItemNotes} />}
                    <Button size="sm" variant="ghost" onClick={() => openHistoryFor(it)} title="History" className="p-1 h-7" data-testid={`history-${it.item_id}`}><History className="w-3 h-3" /></Button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function HistoricalDialog({ data, onClose }) {
  if (!data) return null;
  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl" data-testid="historical-popup">
        <DialogHeader>
          <DialogTitle>Item History — {data.item?.description || "Item"}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-muted-foreground mb-3">Qty: {data.item?.quantity} {data.item?.unit} · Cost code: <span className="font-mono">{data.item?.cost_code || "—"}</span></p>

        {/* Current project bids */}
        <div className="space-y-2">
          <h4 className="text-sm font-semibold flex items-center gap-2">This project — Vendor bids ({data.current_project_bids?.length || 0})</h4>
          {(data.current_project_bids || []).length === 0 && <p className="text-xs text-muted-foreground">No bids for this item yet.</p>}
          {(data.current_project_bids || []).map((b, i) => (
            <div key={i} className="flex items-center justify-between border rounded p-2 text-sm" data-testid={`cur-bid-${i}`}>
              <div>
                <p className="font-medium">{b.vendor_name} {b.excluded && <Badge className="ml-2 bg-amber-100 text-amber-700">excluded</Badge>}</p>
                <p className="text-xs text-muted-foreground">{b.package_name}</p>
              </div>
              <span className={`font-mono ${b.excluded ? "line-through text-muted-foreground" : "font-semibold"}`}>{fmtCurrency(b.unit_price)}/u</span>
            </div>
          ))}
        </div>

        {/* Historical */}
        <div className="space-y-2 mt-4">
          <h4 className="text-sm font-semibold">Historical projects (avg {fmtCurrency(data.historical?.avg_unit_price)} · range {fmtCurrency(data.historical?.min_unit_price)} — {fmtCurrency(data.historical?.max_unit_price)})</h4>
          <div className="max-h-72 overflow-y-auto space-y-2">
            {(data.historical?.projects || []).map(p => (
              <div key={p.project_id} className="border rounded p-2 text-sm" data-testid={`hist-${p.project_id}`}>
                <div className="flex items-center justify-between">
                  <div>
                    <p className="font-medium">{p.project_name}</p>
                    <p className="text-xs text-muted-foreground">{p.vendor} · {p.location} · {p.year}</p>
                  </div>
                  <div className="text-right">
                    <p className="font-mono font-semibold">{fmtCurrency(p.unit_price)}/{p.unit}</p>
                    <p className="text-xs text-muted-foreground">Total: {fmtCurrency(p.total)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* =================== CHANGE LOG DIALOG =================== */
const CHANGELOG_STEPS = [
  { key: "all", label: "All" },
  { key: "scope", label: "Scope" },
  { key: "hidden", label: "Hidden" },
  { key: "compliance", label: "Compliance" },
  { key: "cost_codes", label: "Cost Codes" },
  { key: "pricing", label: "Pricing" },
  { key: "bidding", label: "Bidding" },
  { key: "final", label: "Final" },
  { key: "meeting", label: "Meeting" },
];

function ChangeLogDialog({ caseId, onClose }) {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState("all");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/cases/${caseId}/audit-log${step !== "all" ? `?step=${step}` : ""}`);
      setLogs(r.data);
    } catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [caseId, step]);

  useEffect(() => { load(); }, [load]);

  const exportCSV = () => {
    const headers = ["timestamp", "action", "entity_type", "entity_id", "old_value", "new_value", "reason"];
    const rows = logs.map(l => headers.map(h => `"${String(l[h] ?? "").replace(/"/g, '""')}"`).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `changelog_${caseId}.csv`; a.click();
    toast.success("CSV downloaded");
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="changelog-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><ScrollText className="w-5 h-5" /> Change Log</DialogTitle>
        </DialogHeader>

        <div className="flex flex-wrap gap-2 items-center mb-3">
          {CHANGELOG_STEPS.map(s => (
            <Button key={s.key} size="sm" variant={step === s.key ? "default" : "outline"} onClick={() => setStep(s.key)} data-testid={`changelog-filter-${s.key}`}>
              {s.label}
            </Button>
          ))}
          <div className="flex-1" />
          <Button size="sm" variant="outline" onClick={exportCSV} className="gap-1" data-testid="changelog-export-btn"><FileDown className="w-3 h-3" /> CSV</Button>
        </div>

        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" /></div>
          ) : logs.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">No events for this filter</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-left text-xs text-muted-foreground">
                  <th className="py-1 pr-2">When</th>
                  <th className="py-1 pr-2">Action</th>
                  <th className="py-1 pr-2">Entity</th>
                  <th className="py-1 pr-2">Before</th>
                  <th className="py-1 pr-2">After</th>
                  <th className="py-1 pr-2">Reason</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l, i) => (
                  <tr key={i} className="border-b" data-testid={`changelog-row-${i}`}>
                    <td className="py-2 pr-2 text-xs whitespace-nowrap">{new Date(l.timestamp).toLocaleString()}</td>
                    <td className="py-2 pr-2 text-xs"><Badge variant="outline">{l.action || l.decision_type}</Badge></td>
                    <td className="py-2 pr-2 text-xs">{l.entity_type}</td>
                    <td className="py-2 pr-2 text-xs max-w-[200px] truncate text-muted-foreground">{stringify(l.old_value)}</td>
                    <td className="py-2 pr-2 text-xs max-w-[200px] truncate">{stringify(l.new_value)}</td>
                    <td className="py-2 pr-2 text-xs max-w-[200px] truncate">{l.reason || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function stringify(v) {
  if (v == null) return "—";
  if (typeof v === "string") return v;
  try { return JSON.stringify(v); } catch { return String(v); }
}

/* =================== DECISION CANDIDATES DIALOG =================== */
function CandidatesDialog({ caseData, extractDecisions, tagFile, decideCandidate, processing, onClose }) {
  const files = caseData.files || [];
  const meetingFiles = files.filter(f => f.file_type === "meeting_note");
  const cands = caseData.decision_candidates || [];
  const pending = cands.filter(c => c.status === "pending");
  const approved = cands.filter(c => c.status === "approved");

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[85vh] overflow-hidden flex flex-col" data-testid="candidates-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Inbox className="w-5 h-5" /> Meeting Decisions</DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 space-y-4">
          {/* File tagger */}
          <div>
            <h4 className="text-sm font-semibold mb-2">Mark a file as Meeting Note</h4>
            {files.length === 0 ? (
              <p className="text-xs text-muted-foreground">Upload a file in Setup first.</p>
            ) : (
              <div className="space-y-1">
                {files.map(f => (
                  <div key={f.id} className="flex items-center gap-2 border rounded p-2 text-sm" data-testid={`file-tag-${f.id}`}>
                    <FileIcon name={f.original_name} />
                    <span className="flex-1 truncate">{f.original_name}</span>
                    {f.file_type === "meeting_note" ? (
                      <Badge className="bg-blue-100 text-blue-700">Meeting Note</Badge>
                    ) : (
                      <Button size="sm" variant="outline" onClick={() => tagFile(f.id, "meeting_note")}>Tag as Meeting</Button>
                    )}
                    {f.file_type === "meeting_note" && (
                      <LoadingButton size="sm" onClick={() => extractDecisions(f.id)} loading={processing} className="gap-1" data-testid={`extract-${f.id}`}>
                        <Sparkles className="w-3 h-3" /> Extract
                      </LoadingButton>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Pending */}
          <div>
            <h4 className="text-sm font-semibold mb-2">Pending review ({pending.length})</h4>
            {pending.length === 0 ? (
              <p className="text-xs text-muted-foreground">No pending decisions.</p>
            ) : (
              <div className="space-y-2">
                {pending.map(c => (
                  <div key={c.id} className="border rounded p-3" data-testid={`candidate-${c.id}`}>
                    <p className="font-medium text-sm">{c.summary}</p>
                    <div className="text-xs text-muted-foreground mt-1 flex items-center gap-1 flex-wrap">
                      <Badge variant="outline">{c.target_entity}</Badge>
                      {c.rationale && <span className="italic">"{c.rationale}"</span>}
                    </div>
                    <p className="text-xs text-muted-foreground mt-1">From: {c.file_name}</p>
                    <div className="flex gap-2 mt-2">
                      <Button size="sm" onClick={() => decideCandidate(c.id, "approve")} className="gap-1" data-testid={`approve-${c.id}`}>
                        <BadgeCheck className="w-3 h-3" /> Approve
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => decideCandidate(c.id, "reject")} data-testid={`reject-${c.id}`}>Reject</Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Approved */}
          {approved.length > 0 && (
            <div>
              <h4 className="text-sm font-semibold mb-2">Approved ({approved.length})</h4>
              <div className="space-y-1">
                {approved.map(c => (
                  <div key={c.id} className="text-xs flex items-center gap-2 border rounded p-2">
                    <BadgeCheck className="w-3 h-3 text-green-600" />
                    <span className="flex-1">{c.summary}</span>
                    <span className="text-muted-foreground">{c.file_name}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

/* =================== REVISIONS DIALOG =================== */
function RevisionsDialog({ caseId, onClose }) {
  const [revs, setRevs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [newRev, setNewRev] = useState({ label: "", description: "" });
  const [fromRev, setFromRev] = useState("");
  const [toRev, setToRev] = useState("");
  const [diff, setDiff] = useState(null);
  const [diffLoading, setDiffLoading] = useState(false);
  const [narrative, setNarrative] = useState("");
  const [narrativeLoading, setNarrativeLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await axios.get(`${API}/cases/${caseId}/revisions`); setRevs(r.data); }
    catch { toast.error("Failed to load"); }
    finally { setLoading(false); }
  }, [caseId]);

  useEffect(() => { load(); }, [load]);

  const create = async () => {
    if (!newRev.label) return toast.error("Label required");
    try { await axios.post(`${API}/cases/${caseId}/revisions`, newRev); toast.success("Revision created"); setNewRev({ label: "", description: "" }); setCreating(false); load(); }
    catch { toast.error("Create failed"); }
  };

  const runDiff = async () => {
    setDiffLoading(true);
    setNarrative("");
    try {
      const params = new URLSearchParams();
      if (fromRev && fromRev !== "current") params.append("from_rev", fromRev);
      if (toRev && toRev !== "current") params.append("to_rev", toRev);
      const r = await axios.get(`${API}/cases/${caseId}/diff?${params}`);
      setDiff(r.data);
    } catch { toast.error("Diff failed"); }
    finally { setDiffLoading(false); }
  };

  const runNarrative = async () => {
    setNarrativeLoading(true);
    try {
      const fd = new FormData();
      if (fromRev && fromRev !== "current") fd.append("from_rev", fromRev);
      if (toRev && toRev !== "current") fd.append("to_rev", toRev);
      const r = await axios.post(`${API}/cases/${caseId}/diff/storytelling`, fd);
      setNarrative(r.data.narrative);
    } catch { toast.error("AI failed"); }
    finally { setNarrativeLoading(false); }
  };

  const renderDiffSection = (title, section) => {
    const total = (section?.added?.length || 0) + (section?.removed?.length || 0) + (section?.changed?.length || 0);
    if (total === 0) return null;
    return (
      <div className="border rounded p-3" data-testid={`diff-${title.toLowerCase()}`}>
        <h4 className="font-semibold text-sm mb-2">{title} <Badge variant="outline">{total} changes</Badge></h4>
        {section.added?.length > 0 && (
          <div className="text-xs mb-1">
            <span className="text-green-700 font-medium">+ Added ({section.added.length})</span>
            <ul className="ml-3 list-disc">{section.added.slice(0, 6).map((a, i) => <li key={i}>{a.description || a.original_name || a.name || JSON.stringify(a).slice(0, 60)}</li>)}</ul>
          </div>
        )}
        {section.removed?.length > 0 && (
          <div className="text-xs mb-1">
            <span className="text-red-700 font-medium">− Removed ({section.removed.length})</span>
            <ul className="ml-3 list-disc">{section.removed.slice(0, 6).map((a, i) => <li key={i}>{a.description || a.original_name || a.name || JSON.stringify(a).slice(0, 60)}</li>)}</ul>
          </div>
        )}
        {section.changed?.length > 0 && (
          <div className="text-xs">
            <span className="text-amber-700 font-medium">~ Changed ({section.changed.length})</span>
            <ul className="ml-3 list-disc">
              {section.changed.slice(0, 6).map((c, i) => (
                <li key={i}>
                  {c.description}: {Object.entries(c.changes).map(([f, v]) => <span key={f} className="mr-2 font-mono">{f}: {String(v.old)} → {String(v.new)}</span>)}
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-hidden flex flex-col" data-testid="revisions-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><GitBranch className="w-5 h-5" /> Revisions</DialogTitle>
        </DialogHeader>

        <div className="overflow-y-auto flex-1 space-y-4">
          {/* Revision list + new */}
          <div className="flex items-center justify-between">
            <h4 className="font-semibold text-sm">{revs.length} revision(s)</h4>
            <Button size="sm" variant="outline" onClick={() => setCreating(!creating)} className="gap-1" data-testid="new-rev-btn"><Plus className="w-3 h-3" /> New Revision</Button>
          </div>

          {creating && (
            <div className="border rounded p-3 bg-muted/30 space-y-2">
              <Input placeholder="Label (e.g. R1)" value={newRev.label} onChange={(e) => setNewRev({ ...newRev, label: e.target.value })} data-testid="rev-label-input" />
              <Textarea rows={2} placeholder="Description / context" value={newRev.description} onChange={(e) => setNewRev({ ...newRev, description: e.target.value })} />
              <div className="flex gap-2 justify-end">
                <Button size="sm" variant="ghost" onClick={() => setCreating(false)}>Cancel</Button>
                <Button size="sm" onClick={create} data-testid="save-rev-btn">Save</Button>
              </div>
            </div>
          )}

          {loading ? <Loader2 className="w-5 h-5 animate-spin mx-auto" /> : (
            <div className="space-y-1">
              {revs.map((r) => (
                <div key={r.id} className="flex items-center gap-2 border rounded p-2 text-sm" data-testid={`rev-${r.id}`}>
                  <Badge variant="outline" className="font-mono">{r.label}</Badge>
                  <span className="flex-1 truncate">{r.description || <em className="text-muted-foreground">no description</em>}</span>
                  <span className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</span>
                </div>
              ))}
              {revs.length === 0 && <p className="text-xs text-muted-foreground text-center py-2">No revisions yet. Create one to enable diff.</p>}
            </div>
          )}

          {/* Diff selector */}
          <div className="border-t pt-4 space-y-3">
            <h4 className="font-semibold text-sm">Compare</h4>
            <div className="flex flex-wrap gap-2 items-center">
              <Select value={fromRev} onValueChange={setFromRev}>
                <SelectTrigger className="w-44" data-testid="diff-from-select"><SelectValue placeholder="From" /></SelectTrigger>
                <SelectContent>
                  {revs.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <span>↔</span>
              <Select value={toRev} onValueChange={setToRev}>
                <SelectTrigger className="w-44" data-testid="diff-to-select"><SelectValue placeholder="To" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="current">Current state</SelectItem>
                  {revs.map(r => <SelectItem key={r.id} value={r.id}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <Button onClick={runDiff} disabled={diffLoading} data-testid="run-diff-btn">{diffLoading ? "Computing..." : "Diff"}</Button>
              {diff && <LoadingButton onClick={runNarrative} loading={narrativeLoading} variant="outline" className="gap-1" data-testid="run-narrative-btn"><Sparkles className="w-3 h-3" /> AI Storytelling</LoadingButton>}
            </div>

            {narrative && (
              <div className="border rounded p-3 bg-muted/30 text-sm whitespace-pre-wrap" data-testid="diff-narrative">{narrative}</div>
            )}

            {diff && (
              <div className="space-y-2">
                <div className="text-xs text-muted-foreground">Scope items: {diff.totals.old_scope_count} → {diff.totals.new_scope_count} · Files: {diff.totals.old_files_count} → {diff.totals.new_files_count}</div>
                {renderDiffSection("Documents", diff.documents)}
                {renderDiffSection("Scope", diff.scope)}
                {renderDiffSection("Hidden Work", diff.hidden_work)}
                {renderDiffSection("Compliance", diff.compliance)}
                {renderDiffSection("Bids", diff.bids)}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
function NotesIcons({ item, updateItemNotes }) {
  const [open, setOpen] = useState(null); // 'internal' | 'external' | null
  const [draft, setDraft] = useState({ internal_note: item.internal_note || "", external_note: item.external_note || "" });
  useEffect(() => { setDraft({ internal_note: item.internal_note || "", external_note: item.external_note || "" }); }, [item]);

  const save = async () => {
    await updateItemNotes(item.id, draft);
    setOpen(null);
  };

  return (
    <div className="flex items-center gap-1">
      <Button size="sm" variant="ghost" className={`p-1 h-7 ${item.internal_note ? "text-amber-600" : "text-muted-foreground"}`}
        title="Internal note (NOT in proposal)" onClick={() => setOpen("internal")} data-testid={`note-internal-${item.id}`}>
        <NotebookPen className="w-3.5 h-3.5" />
      </Button>
      <Button size="sm" variant="ghost" className={`p-1 h-7 ${item.external_note ? "text-blue-600" : "text-muted-foreground"}`}
        title="External note (in proposal)" onClick={() => setOpen("external")} data-testid={`note-external-${item.id}`}>
        <MessageSquare className="w-3.5 h-3.5" />
      </Button>

      {open && (
        <Dialog open={true} onOpenChange={() => setOpen(null)}>
          <DialogContent className="max-w-md" data-testid={`note-dialog-${item.id}`}>
            <DialogHeader>
              <DialogTitle>{open === "internal" ? "Internal Note (private)" : "External Note (proposal)"}</DialogTitle>
            </DialogHeader>
            <p className="text-xs text-muted-foreground">{open === "internal" ? "Visible only to the internal team. Will NOT appear in the Word export." : "Will appear in the technical proposal."}</p>
            <Textarea
              rows={6}
              value={open === "internal" ? draft.internal_note : draft.external_note}
              onChange={(e) => setDraft({ ...draft, [open === "internal" ? "internal_note" : "external_note"]: e.target.value })}
              data-testid={`note-textarea-${item.id}`}
            />
            <div className="flex justify-end gap-2 mt-2">
              <Button variant="ghost" onClick={() => setOpen(null)}>Cancel</Button>
              <Button onClick={save} data-testid={`note-save-${item.id}`}>Save</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
